'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_THRESHOLD = 150;

/**
 * Duration (ms) to keep user-scroll detection active after the last
 * wheel/touch event. Covers momentum/inertia scrolling where scroll
 * events continue firing after the user lifts their finger.
 */
const USER_SCROLL_TIMEOUT_MS = 150;

interface UseAutoScrollOptions {
	/** Distance from bottom (px) within which user is considered "at bottom". Default: 150 */
	threshold?: number;
	/** Dependency that triggers a scroll attempt (e.g., allMessages array ref). */
	scrollTrigger?: unknown;
}

interface UseAutoScrollReturn {
	/** Attach to the scrollable container element */
	scrollContainerRef: React.RefObject<HTMLElement | null>;
	/** Attach to the bottom sentinel element */
	bottomRef: React.RefObject<HTMLDivElement | null>;
	/** Whether the user is currently near the bottom */
	isAtBottom: boolean;
	/** Programmatically scroll to bottom and re-engage auto-scroll */
	scrollToBottom: () => void;
}

export function useAutoScroll({
	threshold = DEFAULT_THRESHOLD,
	scrollTrigger
}: UseAutoScrollOptions = {}): UseAutoScrollReturn {
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const bottomRef = useRef<HTMLDivElement | null>(null);

	// Hot-path ref — checked during rAF scroll decision (no re-render cost)
	const isAtBottomRef = useRef(true);
	// State — drives button visibility (only updated when value actually changes)
	const [isAtBottom, setIsAtBottom] = useState(true);

	// Track initial mount to use instant scroll on first render
	const isInitialMount = useRef(true);

	// Distinguishes user-initiated scrolls from programmatic scrollIntoView().
	// wheel/touchmove events only fire from user actions, never from programmatic scrolls.
	// Without this, smooth scroll animations fire continuous scroll events that
	// falsely re-engage auto-scroll, making it impossible to scroll up during streaming.
	const userScrollingRef = useRef(false);
	const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	// Scroll event listener — only processes user-initiated scrolls
	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const handleScroll = () => {
			// Ignore scroll events from programmatic scrollIntoView animations.
			// Only process user-initiated scrolls (preceded by wheel/touchmove).
			if (!userScrollingRef.current) return;

			const { scrollTop, scrollHeight, clientHeight } = container;
			const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
			const atBottom = distanceFromBottom <= threshold;

			isAtBottomRef.current = atBottom;
			setIsAtBottom((prev) => (prev !== atBottom ? atBottom : prev));
		};

		// Mark that a user-initiated scroll is happening.
		// Extends the window on each event to cover momentum/inertia scrolling.
		const onUserScroll = () => {
			userScrollingRef.current = true;
			clearTimeout(userScrollTimeoutRef.current);
			userScrollTimeoutRef.current = setTimeout(() => {
				userScrollingRef.current = false;
			}, USER_SCROLL_TIMEOUT_MS);
		};

		container.addEventListener('scroll', handleScroll, { passive: true });
		container.addEventListener('wheel', onUserScroll, { passive: true });
		container.addEventListener('touchmove', onUserScroll, { passive: true });

		return () => {
			container.removeEventListener('scroll', handleScroll);
			container.removeEventListener('wheel', onUserScroll);
			container.removeEventListener('touchmove', onUserScroll);
			clearTimeout(userScrollTimeoutRef.current);
		};
	}, [threshold]);

	// Auto-scroll when scrollTrigger changes (only if user is at bottom)
	useEffect(() => {
		// On initial mount, always scroll to bottom instantly (no smooth animation)
		if (isInitialMount.current) {
			isInitialMount.current = false;
			requestAnimationFrame(() => {
				bottomRef.current?.scrollIntoView({ behavior: 'auto' });
			});
			return;
		}

		// Skip auto-scroll if user has scrolled up
		if (!isAtBottomRef.current) return;

		// Double rAF: scroll after React DOM commit AND browser paint
		const rafId = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				// Skip if container is already at the bottom (content didn't grow visually).
				// Covers cases like response text streaming while user is on a different tab
				// — the trigger fires but the unmounted tab content doesn't affect scroll height.
				const container = scrollContainerRef.current;
				if (container) {
					const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
					if (distance <= 1) return;
				}
				bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
			});
		});
		return () => cancelAnimationFrame(rafId);
	}, [scrollTrigger]);

	// Programmatic scroll to bottom — re-engages auto-scroll
	const scrollToBottom = useCallback(() => {
		isAtBottomRef.current = true;
		setIsAtBottom(true);
		bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	return {
		scrollContainerRef,
		bottomRef,
		isAtBottom,
		scrollToBottom
	};
}
